import { useEffect, useState, useRef, useCallback } from 'react';
import { Trash2, GripVertical, Upload } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import {
  getVehicleImages, uploadVehicleImage, deleteVehicleImage, reorderVehicleImages,
  type VehicleImage,
} from '../api';

interface Props {
  vehicleId: number;
  viewOnly?: boolean;
}

// Galleri-uploader til Public listing-tab. Drag-to-reorder, klik upload, slet pr billede.
// Billeder gemmes i rfo_vehicle_images og uploades til Bunny under customer-{id}/vehicles/{id}/images/.
export default function VehicleGallery({ vehicleId, viewOnly = false }: Props) {
  const { showError, showSuccess } = useToast();
  const [images, setImages] = useState<VehicleImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try { setImages(await getVehicleImages(vehicleId)); }
    catch (e) { showError(e instanceof Error ? e.message : 'Kunne ikke hente galleri'); }
    finally { setLoading(false); }
  }, [vehicleId, showError]);

  useEffect(() => { reload(); }, [reload]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await uploadVehicleImage(vehicleId, file);
      await reload();
      showSuccess('Billede tilfojet');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Upload fejlede');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (img: VehicleImage) => {
    if (!confirm('Slet dette billede?')) return;
    try {
      await deleteVehicleImage(vehicleId, img.id);
      await reload();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke slette');
    }
  };

  const handleDrop = async (toIndex: number) => {
    if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); return; }
    const next = [...images];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    setImages(next);
    setDragIndex(null);
    try {
      await reorderVehicleImages(vehicleId, next.map(i => i.id));
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Kunne ikke gemme raekkefoelge');
      await reload();  // gendan original-rakkefoelge ved fejl
    }
  };

  if (loading) return <p className="text-sm text-gray-400">...</p>;

  return (
    <div className="space-y-3">
      {images.length === 0 ? (
        <p className="text-sm text-gray-400">Ingen billeder endnu. Tilfoej et eller flere herunder.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map((img, i) => (
            <div key={img.id}
              draggable={!viewOnly}
              onDragStart={() => setDragIndex(i)}
              onDragOver={e => e.preventDefault()}
              onDrop={() => handleDrop(i)}
              className={`relative group aspect-[4/3] bg-cream-100 rounded-lg overflow-hidden border ${
                dragIndex === i ? 'opacity-40' : 'border-ink-100'
              } ${!viewOnly ? 'cursor-move' : ''}`}>
              <img src={img.image_url} alt="" className="w-full h-full object-cover" />
              {!viewOnly && (
                <>
                  <div className="absolute top-1.5 left-1.5 bg-black/50 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical size={12} />
                  </div>
                  <button type="button" onClick={() => handleDelete(img)}
                    className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-rose-600 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Slet">
                    <Trash2 size={12} />
                  </button>
                </>
              )}
              <div className="absolute bottom-1.5 left-1.5 text-[10px] text-white bg-black/50 rounded px-1.5 py-0.5">
                {i + 1}
              </div>
            </div>
          ))}
        </div>
      )}

      {!viewOnly && (
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) handleUpload(f); }} />
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-dashed border-gray-300 text-sm text-gray-600 hover:bg-gray-50">
            <Upload size={14} />
            {uploading ? 'Uploader...' : 'Tilfoej billede'}
          </button>
          <p className="text-xs text-gray-400 mt-1">Tip: traek billeder for at omarrangere. Forste billede vises stoerst paa detalje-siden.</p>
        </div>
      )}
    </div>
  );
}
